import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import VoiceInputButton from "@/components/VoiceInputButton";
import { LOCATION_OPTIONS, DEVICE_OPTIONS, KIND_OPTIONS } from "@/lib/enums";
import { Sparkles } from "lucide-react";
import { t } from "@/lib/i18n";

const today = () => new Date().toISOString().slice(0, 10);

const ReadingForm = ({ onCreated, hasProperty }) => {
  const [pciL, setPciL] = useState("");
  const [date, setDate] = useState(today());
  const [location, setLocation] = useState("basement");
  const [device, setDevice] = useState("short_term_kit");
  const [kind, setKind] = useState("screening");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!hasProperty) {
      setError(t("Please add your property above before recording a reading."));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/readings", {
        pci_l: Number(pciL),
        reading_date: date,
        location_in_home: location,
        device_type: device,
        kind,
        notes: notes.trim() || null,
      });
      onCreated(data);
      setPciL("");
      setNotes("");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="card-flat space-y-5"
      data-testid={TESTIDS.readingForm}
    >
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#2F524A]" />
        <h2 className="font-serif text-2xl">{t("Enter a reading")}</h2>
      </div>
      <p className="text-sm text-[#5C6661]">
        {t("Record every radon test on this property — screening, confirmatory, post-mitigation, or long-term monitoring.")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="eyebrow block mb-2">
            {t("Radon reading (pCi/L)")}{" "}<span className="text-[#9B2C2C]">*</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              required
              step="0.1"
              min={0}
              max={1000}
              value={pciL}
              onChange={(e) => setPciL(e.target.value)}
              className="input-field !text-lg font-serif flex-1"
              placeholder={t("e.g. 5.8")}
              data-testid={TESTIDS.readingPciL}
            />
            <VoiceInputButton
              mode="number"
              onResult={(n) => setPciL(String(n))}
              testid="voice-mic-pci-l"
            />
          </div>
          <p className="text-xs text-[#5C6661] mt-1.5">
            EPA action level is 4.0 pCi/L. No level is completely safe. Tap the mic and say something like &ldquo;three point eight&rdquo;.
          </p>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Test date")}{" "}<span className="text-[#9B2C2C]">*</span></label>
          <input
            type="date"
            required
            max={today()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input-field"
            data-testid={TESTIDS.readingDate}
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Location in home")}</label>
          <div className="flex items-center gap-2">
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="input-field flex-1"
              data-testid={TESTIDS.readingLocation}
            >
              {LOCATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <VoiceInputButton
              mode="select"
              options={LOCATION_OPTIONS}
              onResult={(v) => setLocation(v)}
              testid="voice-mic-location"
            />
          </div>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Test type / device")}</label>
          <div className="flex items-center gap-2">
            <select
              value={device}
              onChange={(e) => setDevice(e.target.value)}
              className="input-field flex-1"
              data-testid={TESTIDS.readingDevice}
            >
              {DEVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <VoiceInputButton
              mode="select"
              options={DEVICE_OPTIONS}
              onResult={(v) => setDevice(v)}
              testid="voice-mic-device"
            />
          </div>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Kind of reading")}</label>
          <div className="flex items-center gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="input-field flex-1"
              data-testid={TESTIDS.readingKind}
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <VoiceInputButton
              mode="select"
              options={KIND_OPTIONS}
              onResult={(v) => setKind(v)}
              testid="voice-mic-kind"
            />
          </div>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Notes (optional)")}</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field flex-1"
              placeholder={t("Weather, windows open, etc.")}
              maxLength={500}
              data-testid={TESTIDS.readingNotes}
            />
            <VoiceInputButton
              mode="text"
              onResult={(t) => setNotes((prev) => (prev ? prev.trim() + " " + t : t))}
              testid="voice-mic-notes"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || !hasProperty}
          className="btn-primary inline-flex items-center gap-2"
          data-testid={TESTIDS.readingSubmit}
        >
          {saving ? t("Saving…") : t("Save reading")}
        </button>
      </div>
    </form>
  );
};

export default ReadingForm;
